/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
// src/themes/theme1/hooks/useStoreBootstrap.js
import { useEffect, useState } from "react";
import {
  getItems, getCategories, getCoupons, getTenantDetails, getBanners,
  getTags, getCities, getStoreReviews, normalizeTenantDetails
} from "../../utils/storeApi";

export function useStoreBootstrap(tenantId) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [storeData, setStoreData] = useState({ social: {}, policies: {} });
  const [apiProducts, setApiProducts] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [apiCoupons, setApiCoupons] = useState([]);
  const [apiBanners, setApiBanners] = useState([]);
  const [apiTags, setApiTags] = useState([]);
  const [storeReviews, setStoreReviews] = useState([]);
  const [cities, setCities] = useState([]);
  const [visitsCount, setVisitsCount] = useState(0);
  const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, "");

  useEffect(() => {
    if (!tenantId) return;

    const fetchStoreData = async () => {
      try {
        setLoading(true);
        setError(null);

        const detailsResult = await getTenantDetails(tenantId);
        const tenant = normalizeTenantDetails(detailsResult?.data || {});

        const [
          itemsResult, categoriesResult, couponsResult, bannersResult, tagsResult, reviewsResult
        ] = await Promise.all([
          getItems(tenantId),
          getCategories(tenantId),
          getCoupons(tenantId),
          getBanners(tenantId),
          getTags(tenantId),
          getStoreReviews(tenantId)
        ]);

        const normalizeArray = (src) => {
          if (Array.isArray(src)) return src;
          if (src && Array.isArray(src.data)) return src.data;
          if (src && Array.isArray(src.items)) return src.items;
          if (src && Array.isArray(src.result)) return src.result;
          return [];
        };

        setStoreData({
          tenantId: tenant.tenantId,
          storeName: tenant.storeName,
          storeSlogan: tenant.storeSlogan,
          logo: tenant.logo,
          sectorType: tenant.sectorType,
          themeId: tenant.themeId,
          description: tenant.description,
          social: tenant.social,
          policies: tenant.policies,
          colorName: tenant.colorName,
        });

        setApiProducts(itemsResult?.data || []);
        setApiCategories(normalizeArray(categoriesResult));
        setApiCoupons(normalizeArray(couponsResult));
        setApiBanners(normalizeArray(bannersResult));
        setApiTags(normalizeArray(tagsResult));
        setStoreReviews(reviewsResult?.success ? reviewsResult.data.filter(r => r.isActive === 1) : []);

        setLoading(false);
      } catch (e) {
        setError("فشل تحميل بيانات المتجر");
        setLoading(false);
      }
    };

    fetchStoreData();
  }, [tenantId]);

  useEffect(() => {
    const loadCities = async () => {
      const result = await getCities();
      if (result.success) setCities(result.data);
    };
    loadCities();
  }, []);

  // visits batching: accumulate locally, then flush current + pending to server
  useEffect(() => {
    if (!tenantId) return;
    const sessionKey = `visited_${tenantId}`;
    const pendingKey = `pending_visits_${tenantId}`;

    const incrementPending = () => {
      const pending = Number(localStorage.getItem(pendingKey) || "0") + 1;
      localStorage.setItem(pendingKey, String(pending));
      return pending;
    };

    const getPending = () => Number(localStorage.getItem(pendingKey) || "0");
    const resetPending = () => localStorage.setItem(pendingKey, "0");
    const markVisited = () => sessionStorage.setItem(sessionKey, "1");

    let cancelled = false;

    const flushPendingVisits = async () => {
      try {
        const pending = getPending();
        if (pending <= 0) return;

        const currentRes = await fetch(`${API_BASE_URL}/VisitsCount?tenantId=${tenantId}`, {
          headers: { Accept: "application/json" },
        });

        const currentData = currentRes.ok ? await currentRes.json() : {};
        const currentCount = Number(currentData?.visitsCount || 0);
        const nextCount = currentCount + pending;

        await fetch(`${API_BASE_URL}/VisitsCount`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ tenantId, visitsCount: nextCount }),
        });

        if (!cancelled) setVisitsCount(nextCount);
        resetPending();
      } catch {
        // إذا فشل التحديث لا نمنع عرض البيانات الأساسية
      }
    };

    const registerVisit = () => {
      if (sessionStorage.getItem(sessionKey)) return;
      incrementPending();
      markVisited();
    };

    const flushOnLeave = () => {
      flushPendingVisits().catch(() => {});
    };

    registerVisit();
    flushPendingVisits();

    const intervalId = window.setInterval(() => {
      flushPendingVisits();
    }, 5 * 60 * 1000);

    window.addEventListener("pagehide", flushOnLeave);
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushOnLeave();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      window.removeEventListener("pagehide", flushOnLeave);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [tenantId, API_BASE_URL]);

  // visits GET
  useEffect(() => {
    if (!storeData?.tenantId) return;
    fetch(`${API_BASE_URL}/VisitsCount?tenantId=${storeData.tenantId}`, {
      headers: { Accept: "application/json" },
    })
      .then(res => (res.ok ? res.json() : {}))
      .then(data => setVisitsCount(Number(data.visitsCount || 0)))
      .catch(() => { });
  }, [storeData?.tenantId, API_BASE_URL]);

  return {
    loading, error,
    storeData, apiProducts, apiCategories, apiCoupons, apiBanners, apiTags, storeReviews,
    setStoreReviews, // إذا بدك تحديث من مودال التقييم
    cities, visitsCount
  };
}
